import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Select,
  TextField,
  Tooltip
} from '@material-ui/core';
import createMuiTheme from '@material-ui/core/styles/createTheme';
import Info from '@material-ui/icons/Info';
import SaveIcon from '@material-ui/icons/Save';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {
  addParty,
  deleteParty,
  getDocumentInfo,
  getParties,
  updateParty
} from '../script/document-service.js';
import {addNotifications} from '../store/appActions';
import ContractRadio from './ContractRadio';
import DatePicker from './DatePicker';
import DocumentParties from './DocumentParties';
import NotarizedObjectInput from './NotarizedObjectInput';
import NotarizedTypeInput from './NotarizedTypeInput.jsx';

const theme = createMuiTheme();
const styles = {
  root: {flexGrow: 1},
  paper: {padding: theme.spacing(3, 2)},
  gridContainer: {marginTop: theme.spacing(2)},
  textField: {marginRight: theme.spacing(1), width: 250, marginLeft: 10},
  formControl: {margin: theme.spacing(1), minWidth: 120},
  inputLabel: {width: 300},
  select: {width: 250, marginTop: 24},
  headline: {marginTop: theme.spacing(5), marginBottom: '-2%'},
  multiline: {marginRight: theme.spacing(1), width: 250},
  submitButton: {float: 'right'},
  submitInfo: {
    float: 'right',
    marginRight: 10,
    marginTop: 4
  }
};

/**
 * Map the properties.
 * @param {*} dispatch
 * @return {Function} the add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    addNotifications: (notification) => dispatch(addNotifications(notification))
  };
}

/**
 * Connected component class of DocumentForm.
 *
 */
class ConnectedDocumentForm extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props: props) {
    super(props);
    this.state = {
      validDocumentNumber: true,
      validNotarizedObject: true,
      documentId: props.documentId,
      name: '',
      documentNumber: '',
      contract: 'contractAmongLiving',
      notarizedObject: '',
      notarizedType: '',
      documentDate: undefined,
      testament: '',
      newArchiveVersion: undefined,
      publication: '',
      releaseDate: undefined,
      issueReceivers: '',
      issueAccountNumber: '',
      issueDate: undefined,
      recordNumber: '',
      deliveryDate: undefined,
      reversalDate: undefined,
      comment: '',
      archivingUser: '',
      /* FIXME: Timestamps need to be set by the backend! */
      archivingDate: moment(new Date()).format(locale.dateFormat),
      documentParties: [],
      loading: false,
      openConfirmationDialog: false,
      confirmedDocumentNumber: null
    };
  }

  /**
   * Handle field changes for different input fields.
   *
   * @param {Event} event - Event from field change action.
   */
  handleFieldChange = (event: Event) => {
    this.setState({[event.target.name]: event.target.value});
    if (
      event.target.name === 'contract' &&
      event.target.value === 'contractByDead'
    ) {
      this.setState({notarizedType: 'lawOfSuccession'});
    }
    if (
      event.target.name === 'contract' &&
      event.target.value === 'contractByDead+contractAmongLiving'
    ) {
      this.setState({notarizedType: 'familyInheritanceLaw'});
    }
    if (
      event.target.name === 'contract' &&
      event.target.value === 'contractByDead+contractAmongLiving'
    ) {
      this.setState({notarizedType: 'familyAndSoccessionLaw'});
    }
  };

  /**
   * Handle date change for datepicker elements.
   *
   * @param {Date} date - Selected date from user.
   * @param {String} field - Name of used date picker field.
   */
  handleDateChange = (date: Date, field: String) => {
    if (date === null) {
      this.setState({[field]: date});
    } else if (isNaN(date) == false) {
      const formattedDate = moment(date).format(locale.dateFormat);
      this.setState({[field]: formattedDate});
    } else {
      this.setState({[field]: 'Invalid Date'});
    }
  };

  /**
   * Handle document party add action.
   *
   * @param {Object} party - Document parties to be saved.
   */
  handleAddParty = async (party: Object) => {
    this.state.documentId &&
      party &&
      (await addParty(party, this.state.documentId).then((deleteResponse) => {
        if (deleteResponse.created) {
          this.refreshParties(this.state.documentId);
          this.props.addNotifications([
            new Notification(
              locale.addPartyCompletedNotification,
              'success',
              6000
            )
          ]);
        } else {
          this.props.addNotifications([
            new Notification(locale.addPartyErrorNotification, 'error', 6000)
          ]);
        }
      }));
  };

  /**
   * Handle document party update action.
   *
   * @param {Object} party - Document parties to be saved.
   */
  handleUpdateParty = async (party: Object) => {
    this.state.documentId &&
      party &&
      (await updateParty(party, this.state.documentId).then(
        (deleteResponse) => {
          if (deleteResponse.updated) {
            this.refreshParties(this.state.documentId);
            this.props.addNotifications([
              new Notification(
                locale.updatePartyCompletedNotification,
                'success',
                6000
              )
            ]);
          } else {
            this.props.addNotifications([
              new Notification(
                locale.updatePartyErrorNotification,
                'error',
                6000
              )
            ]);
          }
        }
      ));
  };

  /**
   * Handle document party remove action.
   *
   * @param {String} partyId - Document party to be removed.
   */
  handleDeleteParty = async (partyId: string) => {
    if (this.state.documentParties.length === 1) {
      this.props.addNotifications([
        new Notification(locale.deleteLastPartyErrorNotification, 'error', 6000)
      ]);
    } else if (this.state.documentId && partyId) {
      deleteParty(partyId, this.state.documentId).then((deleteResponse) => {
        if (deleteResponse.deleted) {
          this.refreshParties(this.state.documentId);
          this.props.addNotifications([
            new Notification(
              locale.deletePartyCompletedNotification,
              'success',
              6000
            )
          ]);
        } else {
          this.props.addNotifications([
            new Notification(locale.deletePartyErrorNotification, 'error', 6000)
          ]);
        }
      });
    }
  };

  /**
   * Refresh the parties after a update or for initial loading.
   *
   * @param {String} documentId - Document id reference.
   */
  refreshParties = (documentId: string) => {
    this.state.documentId &&
      getParties(documentId).then((parties) => {
        this.setState({documentParties: parties});
      });
  };

  /**
   * Handles the submit of the add document button.
   *
   */
  handleFormSubmit = async () => {
    if (this.validateFields()) {
      if (this.state.testament === 'deleted') {
        this.setState({
          openConfirmationDialog: true
        });
      } else {
        await this.props.handleDocumentSubmit(this.state);
      }
    }
  };

  /**
   * Validate the input field.
   * @return {boolean} validation result
   */
  validateFields = () => {
    this.setState({validNotarizedObject: this.state.notarizedObject !== ''});
    this.setState({validDocumentNumber: this.state.documentNumber !== ''});
    return (
      this.state.validNotarizedObject &&
      this.state.validDocumentNumber &&
      this.state.documentParties &&
      this.state.documentParties.length > 0
    );
  };

  /**
   * Validate a date input.
   * @param {String} dateString to validate.
   * @return {boolean} validation result
   */
  validateDate(dateString: String) {
    if (dateString === 'Invalid date' || dateString === 'Invalid Date') {
      return false;
    }
    return true;
  }

  // eslint-disable-next-line require-jsdoc
  componentDidMount() {
    if (this.state.documentId) {
      this.setState({loading: true});
      getDocumentInfo(this.state.documentId).then((response) => {
        response.loading = false;
        if (response.notarizedType === null) {
          response.notarizedType = '';
        }
        this.setState(response);
      });
      this.refreshParties(this.state.documentId);
    }
  }

  /**
   * Sub-render method for the form submit button.
   *
   * @return {String} - The component contents.
   */
  renderSubmitButton = () => {
    return (
      <div>
        <Button
          disabled={
            !(
              this.state.documentNumber &&
              this.state.notarizedObject &&
              this.state.documentParties &&
              this.state.documentParties.length > 0 &&
              this.validateDate(this.state.documentDate) &&
              this.validateDate(this.state.newArchiveVersion) &&
              this.validateDate(this.state.releaseDate) &&
              this.validateDate(this.state.issueDate) &&
              this.validateDate(this.state.reversalDate) &&
              this.validateDate(this.state.deliveryDate)
            )
          }
          variant='contained'
          color='primary'
          size='small'
          style={styles.submitButton}
          onClick={(event) => {
            this.handleFormSubmit(event);
          }}
          startIcon={<SaveIcon />}
        >
          {locale.saveButton}
        </Button>
        {!(
          this.state.documentNumber &&
          this.state.notarizedObject &&
          this.state.documentParties &&
          this.state.documentParties.length > 0
        ) && (
          <Tooltip
            style={styles.submitInfo}
            title={locale.documentSubmitValidation}
          >
            <Info />
          </Tooltip>
        )}
      </div>
    );
  };

  /**
   * Sub-render method for the first row of the upload input form.
   *
   * @return {String} - The component contents.
   */
  // eslint-disable-next-line max-lines-per-function
  renderFirstRow = () => {
    return (
      <Grid container item xs={12} spacing={3}>
        <Grid item xs={4}>
          <NotarizedObjectInput
            disabled={this.props.archiveAccess === 'READ'}
            value={{
              notarizedObject: this.state.notarizedObject,
              notarizedType: this.state.notarizedType,
              contract: this.state.contract
            }}
            validNotarizedObject={this.state.validNotarizedObject}
            handleSelect={(value) =>
              this.setState({
                notarizedObject: value ? value.notarizedObject : '',
                notarizedType: value ? value.notarizedType : '',
                contract: value ? value.contract : ''
              })
            }
          />
          <ContractRadio
            handleFieldChange={(event) => {
              this.handleFieldChange(event);
            }}
            disabled={true}
            contract={this.state.contract}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            required
            disabled={this.props.archiveAccess === 'READ'}
            error={!this.state.validDocumentNumber}
            helperText={
              !this.state.validDocumentNumber ? locale.documentNumberEmpty : ''
            }
            id='document-number'
            name='documentNumber'
            label={locale.documentNumber}
            type='text'
            margin='normal'
            style={styles.textField}
            value={this.state.documentNumber}
            onChange={(event) => {
              this.handleFieldChange(event);
            }}
          />
          <FormControl style={styles.formControl}>
            <InputLabel htmlFor='publication' style={styles.inputLabel}>
              {locale.publication}
            </InputLabel>
            <Select
              disabled={this.props.archiveAccess === 'READ'}
              native
              id='publication'
              name='publication'
              value={this.state.publication}
              onChange={(event) => {
                this.handleFieldChange(event);
              }}
              style={styles.select}
              inputProps={{
                id: 'publication'
              }}
            >
              <option value='' />
              <option value='originalCopy'>
                {locale.publicationOriginalCopy}
              </option>
              <option value='promissoryNote'>
                {locale.publicationPromissoryNote}
              </option>
              <option value='copy'>{locale.publicationCopy}</option>
            </Select>
          </FormControl>
          <Grid item xs={4}>
            <NotarizedTypeInput
              contract={this.state.contract}
              disabled={true}
              notarizedType={this.state.notarizedType}
            />
            {(this.state.contract === 'contractByDead' ||
              this.state.contract === 'contractByDead+contractAmongLiving') && (
              <Grid item xs={4}>
                <FormControl style={styles.formControl}>
                  <InputLabel htmlFor='testament' style={styles.inputLabel}>
                    {locale.testament}
                  </InputLabel>
                  <Select
                    disabled={this.props.archiveAccess === 'READ'}
                    native
                    id='testament'
                    name='testament'
                    value={this.state.testament}
                    onChange={(event) => {
                      this.handleFieldChange(event);
                    }}
                    style={styles.select}
                    inputProps={{
                      id: 'testament'
                    }}
                  >
                    <option value='' />
                    <option value='valid'>{locale.testamentValid}</option>
                    <option value='deleted'>{locale.testamentDeleted}</option>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            disabled={this.props.archiveAccess === 'READ'}
            id={'documentDate'}
            label={locale.documentDate}
            handler={this.handleDateChange}
            value={this.state.documentDate}
          />
          <DatePicker
            disabled={this.props.archiveAccess === 'READ'}
            id={'newArchiveVersion'}
            label={locale.newArchiveVersion}
            handler={this.handleDateChange}
            value={this.state.newArchiveVersion}
          />
          <Grid item xs={4}>
            <DatePicker
              disabled={this.props.archiveAccess === 'READ'}
              id={'releaseDate'}
              label={locale.releaseDate}
              handler={this.handleDateChange}
              value={this.state.releaseDate}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  /**
   * Sub-render method for the fifth row of the upload input form.
   *
   * @return {String} - The component contents.
   */
  renderFifthRow = () => {
    return (
      <Grid container item xs={12} spacing={3} style={styles.gridContainer}>
        <Grid item xs={4}>
          <TextField
            disabled={this.props.archiveAccess === 'READ'}
            id='issue-receivers'
            name='issueReceivers'
            label={locale.issueReceivers}
            type='text'
            margin='normal'
            style={styles.textField}
            value={this.state.issueReceivers}
            onChange={(event) => {
              this.handleFieldChange(event);
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            disabled={this.props.archiveAccess === 'READ'}
            id='issue-account-number'
            name='issueAccountNumber'
            label={locale.issueAccountNumber}
            type='text'
            margin='normal'
            style={styles.textField}
            value={this.state.issueAccountNumber}
            onChange={(event) => {
              this.handleFieldChange(event);
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            disabled={this.props.archiveAccess === 'READ'}
            id={'issueDate'}
            label={locale.issueDate}
            handler={this.handleDateChange}
            value={this.state.issueDate}
          />
        </Grid>
      </Grid>
    );
  };

  /**
   * Sub-render method for the sixth row of the upload input form.
   *
   * @return {String} - The component contents.
   */
  renderSixthRow = () => {
    return (
      <Grid container item xs={12} spacing={3}>
        <Grid item xs={4}>
          <TextField
            disabled={this.props.archiveAccess === 'READ'}
            id='record-number'
            name='recordNumber'
            label={locale.recordNumber}
            type='number'
            margin='normal'
            style={styles.textField}
            value={this.state.recordNumber}
            onChange={(event) => {
              this.handleFieldChange(event);
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            disabled={this.props.archiveAccess === 'READ'}
            id={'deliveryDate'}
            label={locale.deliveryDate}
            handler={this.handleDateChange}
            value={this.state.deliveryDate}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            disabled={this.props.archiveAccess === 'READ'}
            id={'reversalDate'}
            label={locale.reversalDate}
            handler={this.handleDateChange}
            value={this.state.reversalDate}
          />
        </Grid>
      </Grid>
    );
  };

  /**
   * Sub-render function for the seventh row of the upload input form.
   *
   * @return {String} - The component contents.
   */
  renderSeventhRow = () => {
    return (
      <React.Fragment>
        <Grid item xs={4}>
          <TextField
            disabled={this.props.archiveAccess === 'READ'}
            multiline
            rows='5'
            id='comment'
            name='comment'
            label={locale.comment}
            style={styles.multiline}
            value={this.state.comment}
            onChange={(event) => {
              this.handleFieldChange(event);
            }}
          />
          <TextField
            disabled
            id='archiving-user'
            name='archivingUser'
            label={locale.archivingUser}
            margin='normal'
            value={this.state.archivingUser}
            style={styles.textField}
            InputProps={{
              readOnly: true
            }}
          />
          <TextField
            disabled
            id='archiving-date'
            name='archivingDate'
            label={locale.archivingDate}
            margin='normal'
            value={this.state.archivingDate}
            style={styles.textField}
            InputProps={{
              readOnly: true
            }}
          />
        </Grid>
      </React.Fragment>
    );
  };

  /**
   * Handles rejection for Testament deletion.
   */
  handleConfirmationDialogClose = () => {
    this.setState({
      openConfirmationDialog: false,
      confirmedDocumentNumber: null
    });
  };

  /**
   * Handles confirmation for Testament deletion.
   *
   */
  handleConfirmationDialogRatify = async () => {
    this.setState({
      openConfirmationDialog: false,
      confirmedDocumentNumber: null
    });
    await this.props.handleDocumentSubmit(this.state);
  };

  /**
   * Saves entered confirmation value for Testament deletion.
   *
   * @param {Object} event - on change event.
   *
   */
  saveConfirmationValue = (event) => {
    this.setState({
      confirmedDocumentNumber: event.currentTarget.value
    });
  };

  /**
   * Renders confirmation dialog for Testaments deletion.
   *
   * @return {string} - confirmation dialog.
   */
  renderConfirmationDialog = () => {
    return (
      <Dialog
        open={this.state.openConfirmationDialog}
        onClose={this.handleConfirmationDialogClose}
        aria-labelledby='confirm-dialog-title'
      >
        <DialogTitle id='confirm-dialog-title'>
          {locale.testamentDeletionDialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {locale.testamentDeletionDialogText}
          </DialogContentText>
          <TextField
            autoFocus
            margin='dense'
            id='dialog-document-number'
            label='Urschriftennummer'
            type='text'
            fullWidth
            onChange={(event) => {
              this.saveConfirmationValue(event);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={
              this.state.confirmedDocumentNumber !== this.state.documentNumber
            }
            onClick={this.handleConfirmationDialogRatify}
            color='primary'
            variant='contained'
          >
            {locale.testamentDeletionYesButtonLabel}
          </Button>
          <Button
            onClick={this.handleConfirmationDialogClose}
            color='primary'
            variant='outlined'
          >
            {locale.testamentDeletionCancelButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  /**
   * React render function.
   *
   * @return {string} - The component contents.
   */
  render() {
    return (
      <div id='root' style={styles.root}>
        {this.state.loading && (
          <CircularProgress
            size={40}
            status={'loading'}
            style={{margin: '20% 50%'}}
            disableShrink
          />
        )}
        {!this.state.loading && (
          <Grid container spacing={1} style={styles.gridContainer}>
            <Paper style={styles.paper}>
              {this.props.archiveAccess === 'WRITE' &&
                this.renderSubmitButton()}
              {this.renderFirstRow()}
              {this.renderFifthRow()}
              <h4 style={styles.headline}>{locale.landRegistry}</h4>
              {this.renderSixthRow()}
              {/* Document parties and additional fields */}
              <Grid
                container
                item
                xs={12}
                spacing={8}
                style={styles.gridContainer}
              >
                <Grid item xs={8}>
                  <DocumentParties
                    disabled={this.props.archiveAccess === 'READ'}
                    addPartyHandler={this.handleAddParty}
                    deletePartyHandler={this.handleDeleteParty}
                    updatePartyHandler={this.handleUpdateParty}
                    parties={this.state.documentParties}
                  />
                </Grid>
                {this.renderSeventhRow()}
              </Grid>
            </Paper>
          </Grid>
        )}
        {this.state.openConfirmationDialog && this.renderConfirmationDialog()}
      </div>
    );
  }
}

ConnectedDocumentForm.propTypes = {
  archiveAccess: PropTypes.string.isRequired,
  addNotifications: PropTypes.func.isRequired,
  documentId: PropTypes.string,
  handleDocumentSubmit: PropTypes.func
};

const DocumentForm = connect(null, mapDispatchToProps)(ConnectedDocumentForm);
export default DocumentForm;
